/**
 * Booking approval queue for tenants that have `requireBookingApproval`
 * enabled. Sorted by appointment time ascending (soonest first), with
 * an urgency badge — red for today, amber for tomorrow.
 *
 * Each row exposes Approve / Reject buttons + an optional response
 * message that gets embedded in the customer's notification email.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Textarea,
} from '@bookaburra/ui';
import { AlertTriangle, ClipboardCheck } from 'lucide-react';
import { useDocumentTitle } from '../lib/use-document-title';
import { fetchBookingTenant } from '../booking-flow/api';

export const Route = createFileRoute('/admin/$slug/approvals')({
  component: ApprovalsPage,
});

interface ApprovalRow {
  id: string;
  serviceId: string;
  serviceName: string;
  rangeStart: number;
  rangeEnd: number;
  createdAt: number;
  urgency: 'urgent' | 'high' | 'normal';
  client: { name: string; email: string | null; phone: string | null } | null;
  staffIds: string[];
  resourceIds: string[];
}

async function fetchApprovals(slug: string): Promise<{ approvals: ApprovalRow[] }> {
  const r = await fetch(`/api/admin/${encodeURIComponent(slug)}/approvals`);
  if (!r.ok) throw new Error(`error ${r.status}`);
  return r.json();
}

async function decide(
  slug: string,
  bookingId: string,
  decision: 'approve' | 'reject',
  responseMessage: string,
): Promise<void> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/approvals/${encodeURIComponent(bookingId)}/${decision}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ responseMessage }),
    },
  );
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
}

function ApprovalsPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const tenantBrand = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantBrand.data
      ? `Approvals · ${tenantBrand.data.tenant.name} Admin`
      : 'Approvals',
  );

  const approvalsQ = useQuery({
    queryKey: ['admin-approvals', slug],
    queryFn: () => fetchApprovals(slug),
  });

  const timezone = tenantBrand.data?.tenant.timezone;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <ClipboardCheck className="h-7 w-7 text-muted-foreground" />
          <span>Approvals</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bookings waiting for your approval. Closest appointments first.
        </p>
      </header>

      {approvalsQ.isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
      ) : approvalsQ.error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t load approvals</AlertTitle>
          <AlertDescription>{(approvalsQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : (approvalsQ.data?.approvals ?? []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No bookings waiting for approval right now.
        </Card>
      ) : (
        <div className="space-y-4">
          {approvalsQ.data!.approvals.map((row) => (
            <ApprovalCard
              key={row.id}
              row={row}
              timezone={timezone}
              onDecided={() => {
                qc.invalidateQueries({ queryKey: ['admin-approvals', slug] });
              }}
              slug={slug}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function ApprovalCard({
  row,
  timezone,
  onDecided,
  slug,
}: {
  row: ApprovalRow;
  timezone: string | undefined;
  onDecided: () => void;
  slug: string;
}) {
  const [responseMessage, setResponseMessage] = useState('');
  const decideMut = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      decide(slug, row.id, action, responseMessage),
    onSuccess: () => onDecided(),
  });

  const when = formatWhen(row.rangeStart, timezone);
  const urgencyTone =
    row.urgency === 'urgent'
      ? 'border-destructive/50 bg-destructive/5'
      : row.urgency === 'high'
        ? 'border-amber-500/50 bg-amber-50'
        : 'border-brand-sage/15 bg-card';

  return (
    <Card className={`p-5 border ${urgencyTone}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{row.serviceName}</p>
          <p className="text-sm text-muted-foreground">{when}</p>
        </div>
        <UrgencyBadge urgency={row.urgency} />
      </div>

      {row.client ? (
        <div className="mt-3 text-sm">
          <p>
            <span className="text-muted-foreground">Customer:</span>{' '}
            <span className="font-medium">{row.client.name}</span>
          </p>
          {row.client.email ? (
            <p className="text-xs text-muted-foreground">{row.client.email}</p>
          ) : null}
          {row.client.phone ? (
            <p className="text-xs text-muted-foreground">{row.client.phone}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Message to customer (optional)
        </label>
        <Textarea
          value={responseMessage}
          onChange={(e) => setResponseMessage(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Optional note — sent in the email back to the customer."
          className="mt-1"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={() => decideMut.mutate('approve')}
          loading={decideMut.isPending && decideMut.variables === 'approve'}
          disabled={decideMut.isPending}
        >
          Approve
        </Button>
        <Button
          variant="destructive"
          onClick={() => decideMut.mutate('reject')}
          loading={decideMut.isPending && decideMut.variables === 'reject'}
          disabled={decideMut.isPending}
        >
          Reject
        </Button>
      </div>

      {decideMut.error ? (
        <Alert variant="destructive" className="mt-3">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t process</AlertTitle>
          <AlertDescription>{(decideMut.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}
    </Card>
  );
}

function UrgencyBadge({ urgency }: { urgency: 'urgent' | 'high' | 'normal' }) {
  if (urgency === 'urgent') return <Badge variant="destructive">Today</Badge>;
  if (urgency === 'high') return <Badge variant="warning">Tomorrow</Badge>;
  return null;
}

function formatWhen(epochMs: number, timezone: string | undefined): string {
  const date = new Date(epochMs);
  const opts: Intl.DateTimeFormatOptions = {
    dateStyle: 'full',
    timeStyle: 'short',
    hour12: false,
    timeZone: timezone,
  };
  return new Intl.DateTimeFormat('en-AU', opts).format(date);
}
